import { useNavigate } from 'react-router-dom';
import { Card } from '@/components/ui';
import { Package, TrendingUp, TrendingDown, AlertCircle, AlertTriangle, Minus } from 'lucide-react';
import type { DashboardStats } from './types';

interface StatsCardsProps {
  stats: DashboardStats;
  previousStats?: DashboardStats;
}

function TrendIndicator({ current, previous, inverse = false }: { current: number; previous?: number; inverse?: boolean }) {
  if (previous === undefined || previous === 0) return null;

  const diff = current - previous;
  const percent = Math.round((diff / previous) * 100);

  if (diff === 0) {
    return (
      <span className="inline-flex items-center gap-0.5 text-xs text-gray-500">
        <Minus size={12} />
        <span>0%</span>
      </span>
    );
  }

  const isPositive = inverse ? diff < 0 : diff > 0;

  return (
    <span className={`inline-flex items-center gap-0.5 text-xs ${isPositive ? 'text-green-600' : 'text-red-600'}`}>
      {diff > 0 ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
      <span>{diff > 0 ? '+' : ''}{percent}%</span>
    </span>
  );
}

export function StatsCards({ stats, previousStats }: StatsCardsProps) {
  const navigate = useNavigate();

  return (
    <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-4 lg:gap-6 mb-6 sm:mb-8">
      <button onClick={() => navigate('/products')} className="text-left">
        <Card padding="sm" hoverable className="h-full">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mb-1">
                Produits
              </p>
              <div className="flex items-baseline gap-2">
                <p className="text-xl sm:text-3xl font-bold text-gray-900 dark:text-white">
                  {stats.totalProducts}
                </p>
                <TrendIndicator current={stats.totalProducts} previous={previousStats?.totalProducts} />
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 hidden sm:block">
                Au catalogue
              </p>
            </div>
            <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/30 rounded-xl flex items-center justify-center flex-shrink-0">
              <Package className="text-blue-600 dark:text-blue-400" size={24} />
            </div>
          </div>
        </Card>
      </button>

      <button onClick={() => navigate('/inventory')} className="text-left">
        <Card padding="sm" hoverable className="h-full">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mb-1">
                Stocks
              </p>
              <div className="flex items-baseline gap-2">
                <p className="text-xl sm:text-3xl font-bold text-gray-900 dark:text-white">
                  {stats.totalStocks}
                </p>
                <TrendIndicator current={stats.totalStocks} previous={previousStats?.totalStocks} />
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 hidden sm:block">
                En inventaire
              </p>
            </div>
            <div className="w-12 h-12 bg-green-100 dark:bg-green-900/30 rounded-xl flex items-center justify-center flex-shrink-0">
              <TrendingUp className="text-green-600 dark:text-green-400" size={24} />
            </div>
          </div>
        </Card>
      </button>

      <button onClick={() => navigate('/inventory')} className="text-left">
        <Card
          padding="sm"
          hoverable
          className={`h-full ${
            stats.criticalStocks > 0 ? 'ring-2 ring-red-200 dark:ring-red-900' : ''
          }`}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mb-1">
                Critiques
              </p>
              <div className="flex items-baseline gap-2">
                <p className="text-xl sm:text-3xl font-bold text-red-600">
                  {stats.criticalStocks}
                </p>
                <TrendIndicator current={stats.criticalStocks} previous={previousStats?.criticalStocks} inverse />
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 hidden sm:block">
                Action requise
              </p>
            </div>
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${
              stats.criticalStocks > 0
                ? 'bg-red-100 dark:bg-red-900/30'
                : 'bg-gray-100 dark:bg-gray-800'
            }`}>
              <AlertCircle className={stats.criticalStocks > 0 ? 'text-red-600 dark:text-red-400' : 'text-gray-400'} size={24} />
            </div>
          </div>
        </Card>
      </button>

      <button onClick={() => navigate('/inventory')} className="text-left">
        <Card
          padding="sm"
          hoverable
          className={`h-full ${
            stats.warningStocks > 0 ? 'ring-2 ring-yellow-200 dark:ring-yellow-900' : ''
          }`}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mb-1">
                Alertes
              </p>
              <div className="flex items-baseline gap-2">
                <p className="text-xl sm:text-3xl font-bold text-yellow-600">
                  {stats.warningStocks}
                </p>
                <TrendIndicator current={stats.warningStocks} previous={previousStats?.warningStocks} inverse />
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 hidden sm:block">
                À surveiller
              </p>
            </div>
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${
              stats.warningStocks > 0
                ? 'bg-yellow-100 dark:bg-yellow-900/30'
                : 'bg-gray-100 dark:bg-gray-800'
            }`}>
              <AlertTriangle className={stats.warningStocks > 0 ? 'text-yellow-600 dark:text-yellow-400' : 'text-gray-400'} size={24} />
            </div>
          </div>
        </Card>
      </button>
    </div>
  );
}
